import React, { useState, useEffect } from 'react';

function OwnerDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const token = localStorage.getItem('token');

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/owner-portal/dashboard`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok) {
          const d = await res.json();
          throw new Error(d.error || 'Failed to load owner dashboard');
        }
        const d = await res.json();
        setData(d);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const formatCurrency = (val) => {
    const num = Number(val) || 0;
    return new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(num);
  };

  if (loading) return <main className="App-main"><p>Loading...</p></main>;
  if (error) {
    return (
      <main className="App-main">
        <div className="report-card" style={{ maxWidth: 600, margin: '2rem auto', padding: '2rem' }}>
          <h2>Owner Portal</h2>
          <p className="form-error">{error}</p>
        </div>
      </main>
    );
  }

  const { properties = [], summary = {} } = data || {};

  return (
    <main className="App-main">
      <div style={{ maxWidth: 800, margin: '2rem auto', padding: '0 1rem' }}>
        <h2 style={{ marginBottom: '0.25rem' }}>Owner Portal</h2>
        <p className="status-text" style={{ marginBottom: '1.5rem' }}>Overview of your managed properties and financials.</p>

        {/* KPIs */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
          <div className="report-card" style={{ padding: '1.25rem', textAlign: 'center' }}>
            <div className="status-text" style={{ fontSize: '0.8rem', marginBottom: '0.25rem' }}>Properties</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 600 }}>{summary.total_properties || properties.length}</div>
          </div>
          <div className="report-card" style={{ padding: '1.25rem', textAlign: 'center' }}>
            <div className="status-text" style={{ fontSize: '0.8rem', marginBottom: '0.25rem' }}>Active tenants</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 600 }}>{summary.total_tenants || 0}</div>
          </div>
          <div className="report-card" style={{ padding: '1.25rem', textAlign: 'center' }}>
            <div className="status-text" style={{ fontSize: '0.8rem', marginBottom: '0.25rem' }}>Revenue (30d)</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 600 }}>{formatCurrency(summary.revenue_30d)}</div>
          </div>
          <div className="report-card" style={{ padding: '1.25rem', textAlign: 'center' }}>
            <div className="status-text" style={{ fontSize: '0.8rem', marginBottom: '0.25rem' }}>Open maintenance</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 600 }}>{summary.open_maintenance || 0}</div>
          </div>
        </div>

        {/* Properties */}
        <div className="report-card" style={{ padding: '1.5rem' }}>
          <h3 style={{ marginBottom: '1rem' }}>Your properties</h3>
          {properties.length === 0 ? (
            <p className="status-text">No properties assigned to you yet.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {properties.map((p) => (
                <div key={p.id} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '0.75rem 1rem', borderRadius: 8,
                  border: '1px solid var(--border-color, #e5e7eb)',
                  background: 'var(--surface-color, #fff)',
                  flexWrap: 'wrap', gap: '0.5rem'
                }}>
                  <div>
                    <strong>{p.address}</strong>
                    {p.city && <span className="status-text" style={{ marginLeft: '0.5rem' }}>{p.city}</span>}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <span className={`report-chip ${p.status === 'available' ? 'report-chip--green' : 'report-chip--muted'}`}>
                      {p.status || 'Active'}
                    </span>
                    {p.rent_amount && (
                      <span className="status-text">{formatCurrency(p.rent_amount)}/mo</span>
                    )}
                    {p.active_tenants > 0 && (
                      <span className="status-text">{p.active_tenants} tenant{p.active_tenants > 1 ? 's' : ''}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recent payments if available */}
        {data?.recent_payments?.length > 0 && (
          <div className="report-card" style={{ padding: '1.5rem', marginTop: '1.5rem' }}>
            <h3 style={{ marginBottom: '1rem' }}>Recent payments</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {data.recent_payments.map((pmt, i) => (
                <div key={i} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '0.5rem 0', borderBottom: '1px solid var(--border-color, #e5e7eb)'
                }}>
                  <div>
                    <span>{pmt.property_address || 'Payment'}</span>
                    <span className="status-text" style={{ marginLeft: '0.5rem' }}>
                      {new Date(pmt.paid_at || pmt.created_at).toLocaleDateString('en-CA', { month: 'short', day: 'numeric' })}
                    </span>
                  </div>
                  <strong>{formatCurrency(pmt.amount)}</strong>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </main>
  );
}

export default OwnerDashboard;
